/**
 * A comprehensive service for interacting with the Shopify Storefront GraphQL API.
 * Handles creating access tokens, updating customer info, and updating addresses.
 */
class ShopifyGraphqlService {
  constructor(shopifyDomain, storefrontAccessToken) {
    this.endpoint = `https://${shopifyDomain}/api/2025-07/graphql.json`;
    this.headers = new Headers({
      "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
      "Content-Type": "application/json",
    });
  }

  /**
   * Executes a GraphQL query.
   * @param {string} query - The GraphQL query string.
   * @param {object} variables - The variables for the query.
   * @returns {Promise<object>} - The JSON response from the API.
   */
  async _fetchGraphQl(query, variables) {
    const body = JSON.stringify({ query, variables });
    const resp = await fetch(this.endpoint, {
      method: "POST",
      headers: this.headers,
      body,
    });

    if (!resp.ok) {
      throw new Error(`Network response was not ok (${resp.status})`);
    }

    const jsonResponse = await resp.json();
    if (jsonResponse.errors && jsonResponse.errors.length) {
      throw new Error(jsonResponse.errors.map((e) => e.message).join("; "));
    }

    // Check for user-level errors within the data payload
    const dataKey = Object.keys(jsonResponse.data)[0];
    const payload = jsonResponse.data[dataKey];
    if (payload && payload.customerUserErrors && payload.customerUserErrors.length) {
      throw new Error(payload.customerUserErrors.map((e) => e.message).join("; "));
    }

    return payload;
  }

  async createAccessToken(email, password) {
    const query = `
      mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input: $input) {
          customerAccessToken { accessToken }
          customerUserErrors { message }
        }
      }`;
    const payload = await this._fetchGraphQl(query, { input: { email, password } });
    return payload.customerAccessToken.accessToken;
  }

  async updateCustomer(customerAccessToken, customerData) {
    const query = `
      mutation customerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
        customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
          customer { id }
          customerUserErrors { message }
        }
      }`;
    return this._fetchGraphQl(query, { customerAccessToken, customer: customerData });
  }

  async updateCustomerAddress(customerAccessToken, addressId, addressData) {
    const query = `
      mutation customerAddressUpdate($address: MailingAddressInput!, $customerAccessToken: String!, $id: ID!) {
        customerAddressUpdate(address: $address, customerAccessToken: $customerAccessToken, id: $id) {
          customerAddress { id }
          customerUserErrors { message }
        }
      }`;
    return this._fetchGraphQl(query, { customerAccessToken, id: addressId, address: addressData });
  }
}


/**
 * Manages customer authentication state and credentials stored in localStorage.
 */
class CustomerAuthManager {
  constructor(options = {}) {
    this.tokenKey = options.tokenKey || "customer_access_token";
    this.passwordKey = options.passwordKey || "customer_password";
    this.emailKey = options.emailKey || "customer_email";
    this.service = new ShopifyGraphqlService(
      options.shopifyDomain,
      options.storefrontAccessToken
    );
  }

  get storedToken() { return localStorage.getItem(this.tokenKey); }
  set storedToken(token) { localStorage.setItem(this.tokenKey, token); }
  get storedPassword() { return localStorage.getItem(this.passwordKey); }
  get storedEmail() { return localStorage.getItem(this.emailKey); }

  clearAll() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.passwordKey);
    localStorage.removeItem(this.emailKey);
  }

  redirectLogout() {
    this.clearAll();
    window.location.href = "/account/logout";
  }

  ensureLoggedIn() {
    if (!this.storedPassword || !this.storedEmail) {
      this.redirectLogout();
      return false;
    }
    return true;
  }

  async getOrCreateToken() {

    if (!this.ensureLoggedIn()) {
      return Promise.reject(new Error("Not logged in"));
    }

    // if (this.storedToken) {
    //   return this.storedToken;
    // }
    const token = await this.service.createAccessToken(this.storedEmail, this.storedPassword);
    this.storedToken = token;
    return token;
  }
}

/**
 * Saves customer credentials from login/signup forms to localStorage.
 */
class CustomerCredentialsManager {
  constructor({
    emailKey = "customer_email",
    passwordKey = "customer_password",
    loginButtonSel = "#login-customer",
    createButtonSel = "#create-customer-account",
  } = {}) {
    this.emailKey = emailKey;
    this.passwordKey = passwordKey;
    this.loginButton = document.querySelector(loginButtonSel);
    this.createButton = document.querySelector(createButtonSel);
    this._bind();
  }

  _bind() {
    [this.loginButton, this.createButton].forEach((btn) => {
      if (btn) btn.addEventListener("click", this._handleClick.bind(this));
    });
  }

  _handleClick(event) {
    const btn = event.currentTarget;
    const form = btn.closest("form");
  
    if (!form) return;

    const emailInput = form.querySelector('input[name="customer[email]"]');
    const passInput = form.querySelector('input[name="customer[password]"]');

    if (emailInput && passInput && emailInput.value && passInput.value) {
      localStorage.setItem(this.emailKey, emailInput.value.trim());
      localStorage.setItem(this.passwordKey, passInput.value);
    }
    // We don't preventDefault, so the form submits normally after we save credentials.
  }
}


// --- Main Application Logic ---
document.addEventListener("DOMContentLoaded", () => {
  const authManager = new CustomerAuthManager({
    shopifyDomain: "mymaskulin.myshopify.com",
    storefrontAccessToken: "62b82afb31b0e76a765c40c929539f60",
  });

  new CustomerCredentialsManager();

  const initialFieldValues = new Map();
  const formContainer = document.querySelector(".contents-container");
  if (!formContainer) return;

  const editBtn = document.getElementById("edit-customer");
  const saveBtn = document.getElementById("save-edit");
  const cancelBtn = document.getElementById("cancel-edit");
  const passwordContainer = document.querySelector(".password-container");

  if (!editBtn || !saveBtn || !cancelBtn) return;

  const getFormElements = () => formContainer.querySelectorAll("input, textarea, select");

  const setViewState = () => {
    getFormElements().forEach((el) => el.setAttribute("disabled", "disabled"));
    [saveBtn, cancelBtn, passwordContainer].forEach((el) => el.classList.add("hidden"));
    editBtn.classList.remove("hidden");
    editBtn.disabled = false;
    editBtn.textContent = "Edit";
    saveBtn.disabled = false;
    saveBtn.textContent = "Save";
  };

  const setEditState = () => {
    initialFieldValues.clear();
    getFormElements().forEach((el) => {
      initialFieldValues.set(el.name, el.value);
      el.removeAttribute("disabled");
    });
    // Re-disable password fields until user wants to change them
    document.querySelector('input[name="customer[current_password]"]').setAttribute("disabled", "disabled");
    // document.querySelector('input[name="customer[password]"]').setAttribute("disabled", "disabled");
    // document.querySelector('input[name="customer[password_confirmation]"]').setAttribute("disabled", "disabled");
    [saveBtn, cancelBtn, passwordContainer].forEach((el) => el.classList.remove("hidden"));
    editBtn.classList.add("hidden");
  };

  // EDIT button click
  editBtn.addEventListener("click", async () => {
    editBtn.disabled = true;
    editBtn.textContent = "Checking…";
    try {
      await authManager.getOrCreateToken();
      setEditState();
    } catch (err) {
      console.error("Auth error:", err);
      alert("Authentication failed. Please log out and log back in.");
      editBtn.textContent = "Retry";
      editBtn.disabled = false;
    }
  });

  // CANCEL button click
  cancelBtn.addEventListener("click", () => {
    getFormElements().forEach((element) => {
      if (initialFieldValues.has(element.name)) {
        element.value = initialFieldValues.get(element.name);
      }
    });
    setViewState();
  });

  // SAVE button click
  saveBtn.addEventListener("click", async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";

    try {
      const token = authManager.storedToken;
      if (!token) throw new Error("Access token not found. Please re-login.");

      const defaultAddressContainer = document.querySelector("[data-default-address-id]");
      const addressId = defaultAddressContainer ? `gid://shopify/MailingAddress/${defaultAddressContainer.dataset.defaultAddressId}?model_name=MailingAddress` : null;

      const customerPayload = {};
      const addressPayload = {};

      // 1. Gather all changed values and map field names correctly
      getFormElements().forEach(el => {
        // Continue only if the field's value has actually changed
        if (el.value !== initialFieldValues.get(el.name)) {
          const match = el.name.match(/(\w+)\[(.*?)\]/); // Extracts 'group[field]'
          if (!match) return;

          const group = match[1]; // 'address' or 'customer'
          const fieldName = match[2]; // e.g., 'first_name'

          // THE FIX: Convert snake_case (first_name) to camelCase (firstName) for the API
          const apiFieldName = fieldName.replace(/_(\w)/g, (_, letter) => letter.toUpperCase());

          if (group === 'address') {
            // Assume shared fields like name/email should update both customer and address
            if (['firstName', 'lastName', 'email'].includes(apiFieldName)) {
              customerPayload[apiFieldName] = el.value;
            }
            // Add all changed address fields to the address payload
            addressPayload[apiFieldName] = el.value;
          }
        }
      });

      // 2. Handle password change specifically
      const newPassInput = document.getElementById("password");
      if (newPassInput.value && newPassInput.value !== initialFieldValues.get(newPassInput.name)) {
        const newPass = newPassInput.value;
        const confirmPass = document.getElementById("password_confirmation").value;
        if (newPass !== confirmPass) {
          throw new Error("Passwords do not match.");
        }
        customerPayload.password = newPass;
      }
      
      // 3. Prepare API calls
      const apiPromises = [];
      if (Object.keys(customerPayload).length > 0) {
        console.log("Updating customer with payload:", customerPayload);
        apiPromises.push(authManager.service.updateCustomer(token, customerPayload));
      }
      if (Object.keys(addressPayload).length > 0 && addressId) {
        console.log("Updating address with payload:", addressPayload);
        apiPromises.push(authManager.service.updateCustomerAddress(token, addressId, addressPayload));
      }
      
      // 4. Execute calls and handle results
      if (apiPromises.length > 0) {
        await Promise.all(apiPromises);
        // On success, update the 'initial values' to the new saved values
        getFormElements().forEach(el => initialFieldValues.set(el.name, el.value));
        alert("Account details saved successfully!");
      }

      setViewState(); // Return to view state on success

    } catch (err) {
      console.error("Save error:", err);
      alert(`Could not save changes: ${err.message}`);
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  });

  // Initial state setup
  setViewState();
});